import { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '@tv2/ui-system/components/Button';
import Modal from '@tv2/lib-offspring/components/messages/Modal';
import { IconButton } from '@tv2/ui-system/components/IconButton';
import { useQueryCallback } from '@tv2/lib-graphql/client';
import { EditNext } from '@tv2/ui-system/icons/next/general/EditNext';
import { DeleteNext } from '@tv2/ui-system/icons/next/general/DeleteNext';
import { AddNext } from '@tv2/ui-system/icons/next/general/AddNext';
import { DetailsPagePromoTextDeckFragment } from '@tv2-cas/offspring/details-page/queries/__generated__/DetailsPage';
import { ModificationTypeDetailsPage } from '../ModificationsProvider';
import { PromoTextDeckEditor, PromoTextDeckInput } from './PromoTextDeckEditor';
import { createPromoPageDocument } from './__generated__/createPromoPage';

type EditableMetaTagsDeckType = {
  title: string | null;
  description: string | null;
};

type PromoTextDeckSectionType = {
  title: string;
  text: string;
};

export type PromoTextDeckType = {
  id: string;
  sections: PromoTextDeckSectionType[];
  header: {
    title: string;
  } | null;
};

export function PromoPageAdminPanel({
  modificationType,
  path,
}: {
  path: string;
  modificationType: ModificationTypeDetailsPage;
}) {
  return <PromoPageAdminPanelInner modificationType={modificationType} path={path} />;
}

export function PromoPageAdminPanelInner({
  modificationType,
  path,
}: {
  path: string;
  modificationType: ModificationTypeDetailsPage;
}) {
  const originalData = useRef(modificationType.data);
  const originalMetadata = originalData.current?.metadata;

  const promoTextDeckData: DetailsPagePromoTextDeckFragment | null =
    originalData.current?.decks?.find(deck => deck?.__typename === 'DetailsPagePromoTextDeck') ??
    null;

  const [metadata, setMetadata] = useState<Partial<EditableMetaTagsDeckType>>({
    title: originalData.current?.metadata?.title ?? null,
    description: originalData.current?.metadata?.description ?? null,
  });
  const [error, setError] = useState<string | undefined>(undefined);
  const [fetching, setFetching] = useState<boolean>(false);
  const [promoTextDeck, setPromoTextDeck] = useState<PromoTextDeckType | null>(
    promoTextDeckData
      ? {
          id: promoTextDeckData.id,
          header: promoTextDeckData.header ? { title: promoTextDeckData.header } : null,
          sections: promoTextDeckData.sections
            ? promoTextDeckData.sections.map(section => ({
                title: section.title ?? '',
                text: section.text ?? '',
              }))
            : [],
        }
      : null
  );
  const [showPromoTextDeck, setShowPromoTextDeck] = useState<boolean>(false);
  const [hasChanges, setHasChanges] = useState<boolean>(false);

  const createPromoPage = useQueryCallback({
    query: createPromoPageDocument,
    variables: {
      page: {
        path,
        decks: promoTextDeck
          ? {
              promoTextDeck: {
                sections: promoTextDeck.sections.map(section => ({
                  title: section.title ?? '',
                  text: section.text ?? '',
                })),
              },
            }
          : null,

        metaDescription:
          originalMetadata?.description !== metadata.description ? metadata.description : undefined,
        metaTitle: originalMetadata?.title !== metadata.title ? metadata.title : undefined,
      },
    },
  });

  const handlePublish = useCallback(async () => {
    setFetching(true);
    const res = await createPromoPage();

    if (res.error) {
      setError(`Der skete en fejl: ${res.error.message ?? ''}`);
    } else {
      setHasChanges(false);
    }

    setFetching(false);
  }, [createPromoPage]);

  const updateMetadata = (newTitle?: string, newDescription?: string) => {
    setMetadata(s => ({
      title: newTitle === undefined ? s.title : newTitle || null,
      description: newDescription === undefined ? s.description : newDescription || null,
    }));
    setHasChanges(true);
  };

  useLeavePageConfirm(
    hasChanges,
    'Du har ikke gemt dine ændringer. Er du sikker på du vil forlade siden?'
  );

  const savePromoText = useCallback<(deck: PromoTextDeckInput) => void>(
    deck => {
      setPromoTextDeck({
        ...deck,
        id: promoTextDeckData?.id ?? '',
        header: null,
      });
      setShowPromoTextDeck(false);
      setHasChanges(true);
    },
    [promoTextDeckData]
  );

  const deletePromoTextDeck = useCallback<(deck: PromoTextDeckType) => void>(deck => {
    setPromoTextDeck(null);
    setHasChanges(true);
  }, []);

  return (
    <>
      {/* <div className="pb-2">{modificationType.data?.title}</div> */}
      <div className="pb-2">
        Meta title
        <input
          data-testid="meta_title_input"
          className="border p-1 w-full"
          type="text"
          value={metadata.title ?? ''}
          onChange={e => updateMetadata(e.target.value)}
        />
      </div>
      <div className="pb-2">
        Meta description
        <textarea
          data-testid="meta_description_input"
          rows={5}
          className="border p-1 w-full"
          value={metadata.description ?? ''}
          onChange={e => updateMetadata(undefined, e.target.value)}
        />
      </div>
      <div className="py-2 mt-2">
        Promo-tekst deck:
        {promoTextDeck ? (
          <div className="flex items-center">
            <div className="pr-3 typo-body-m text-ellipsis whitespace-nowrap min-w-0 overflow-hidden flex-1">
              {`${promoTextDeck.sections?.[0].title} ${promoTextDeck.sections?.[0].text}`}
            </div>
            <div className="flex-none">
              <IconButton
                data-testid="edit_promo_text_deck_btn"
                icon={EditNext}
                color="primary"
                invertIcon
                onClick={() => {
                  setShowPromoTextDeck(true);
                }}
              ></IconButton>
            </div>
            <div className="flex-none">
              <IconButton
                data-testid="delete_promo_text_deck_btn"
                icon={DeleteNext}
                color="primary"
                invertIcon
                onClick={() => {
                  deletePromoTextDeck(promoTextDeck);
                }}
              ></IconButton>
            </div>
          </div>
        ) : null}
      </div>
      {!promoTextDeck ? (
        <div className="mb-8">
          <Button
            data-testid="add_promo_text_deck_btn"
            icon={AddNext}
            color="primary"
            darkMode
            size="small"
            onClick={() => {
              setShowPromoTextDeck(true);
            }}
          >
            Tilføj
          </Button>
        </div>
      ) : null}
      {showPromoTextDeck ? (
        <Modal noDismiss onClose={() => setShowPromoTextDeck(false)}>
          <div className="bg-white overflow-scroll">
            <PromoTextDeckEditor
              deck={promoTextDeck}
              onSave={savePromoText}
              onCancel={() => setShowPromoTextDeck(false)}
            />
          </div>
        </Modal>
      ) : null}
      {hasChanges ? (
        <div className="mb-8">
          <Button
            data-testid="publish_btn"
            color="primary-green"
            size="medium"
            onClick={handlePublish}
            icon={fetching ? Spinner : undefined}
          >
            Udgiv ændringer
          </Button>
          <p
            data-testid="admin_panel_error"
            className="pt-2 pl-1 text-legacy-body-xs text-semantic-error-on-dark"
          >
            {error}
          </p>
        </div>
      ) : null}
    </>
  );
}

const Spinner = ({ className }: { className?: string }) => (
  <svg
    className={`animate-spin -ml-1 mr-3 h-5 w-5 text-white ${className}`}
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
  >
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="white" strokeWidth="4"></circle>
    <path
      className="opacity-75"
      fill="white"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
    ></path>
  </svg>
);

export const useLeavePageConfirm = (
  shouldConfirm: boolean = true,
  _message: string = 'Are you sure want to leave this page?'
) => {
  useEffect(() => {
    if (!shouldConfirm) return;
    const listener: EventListener = e => {
      e.returnValue = true;
      e.preventDefault();
    };
    window.addEventListener('beforeunload', listener);
    return () => {
      window.removeEventListener('beforeunload', listener);
    };
  }, [shouldConfirm]);

  // NB: this only guards reloads, tab close and hard navigations via
  // `beforeunload`. `next/navigation` has no route-change events API, so
  // soft-navigation (<Link>/router.push) is not intercepted for this
  // admin-only panel.
};
